/**
 * The one project-type treatment.
 *
 * WHY THIS EXISTS
 * `shared/schema.ts` declares SIX project types:
 *
 *   projectType: varchar("project_type").$type<"High-rise" | "Low-rise" |
 *     "Towns" | "Stacked Towns" | "Stacked" | "Company">().notNull()
 *
 * and the Add Project dialog offers all six. `pages/ProjectList` knew about
 * four. Found by creating a "Towns" project on 2026-08-26 and watching it fall
 * out of the summary:
 *
 *   ALL PROJECTS 2   LOW-RISE 0   HIGH-RISE 1   STACKED 0
 *
 * The row does not add up, and it cannot: `Towns` and `Stacked Towns` were
 * counted in the total and in none of the three breakdowns, so the percentages
 * were shares of a total containing types with no tile. The same four-value
 * blind spot ran through the whole page — the type filter offered no way to
 * select them, `getProjectTypeLabel` fell through to the raw column value, and
 * `getProjectTypeColor` gave them the grey "unknown" badge instead of the brand
 * colour every other type gets. Towns are a mainstream category for this
 * business, so that is a builder watching part of their portfolio half-render.
 *
 * `stacked` was additionally wrong on its own terms: it matched `"stacked"`
 * exactly, so `"Stacked Towns"` was not stacked either.
 *
 * This is the same shape as [[companyType]] and [[contactChannel]] — a declared
 * union the UI only partly handles — and it lives here for the same reason:
 * a mapping that lives inside one page is a mapping the next page will not use.
 */

import { msg, type Msg } from "@shared/i18n";

/** Every type the schema declares, in the order the UI should present them. */
export const PROJECT_TYPES = [
  'High-rise',
  'Low-rise',
  'Towns',
  'Stacked Towns',
  'Stacked',
  'Company',
] as const;

export type ProjectType = (typeof PROJECT_TYPES)[number];

/**
 * Spellings seen in the column beyond the declared union — the two-letter codes
 * the legacy SaleFish import writes, and the unspaced forms. Keyed uppercase so
 * the lookup is case-insensitive.
 */
const ALIASES: Record<string, ProjectType> = {
  'HIGH-RISE': 'High-rise',
  'HIGHRISE': 'High-rise',
  'HR': 'High-rise',
  'LOW-RISE': 'Low-rise',
  'LOWRISE': 'Low-rise',
  'LR': 'Low-rise',
  'TOWNS': 'Towns',
  'TOWN': 'Towns',
  'TH': 'Towns',
  'STACKED TOWNS': 'Stacked Towns',
  'STACKEDTOWNS': 'Stacked Towns',
  'ST': 'Stacked Towns',
  'STACKED': 'Stacked',
  'SK': 'Stacked',
  'COMPANY': 'Company',
};

/**
 * Fold any spelling onto a canonical type. Returns the input unchanged when it
 * matches nothing, so an unexpected value still renders as itself rather than
 * disappearing.
 */
export function normalizeProjectType(type: string | null | undefined): string {
  if (!type) return '';
  return ALIASES[type.trim().toUpperCase()] ?? type;
}

export function isKnownProjectType(type: string | null | undefined): boolean {
  return PROJECT_TYPES.includes(normalizeProjectType(type) as ProjectType);
}

/** The label to show. Canonical types are already their own label. */
export function projectTypeLabel(type: string | null | undefined): string {
  return normalizeProjectType(type) || '—';
}

/** Display labels for the canonical types, translatable. */
const PROJECT_TYPE_LABELS: Record<ProjectType, Msg> = {
  'High-rise': msg("invProjectTypeHighRise", "High-rise"),
  'Low-rise': msg("invProjectTypeLowRise", "Low-rise"),
  'Towns': msg("invProjectTypeTowns", "Towns"),
  'Stacked Towns': msg("invProjectTypeStackedTowns", "Stacked Towns"),
  'Stacked': msg("invProjectTypeStacked", "Stacked"),
  'Company': msg("invProjectTypeCompany", "Company"),
};

/**
 * The label to show, translatable: a `Msg` for a canonical type (resolve with
 * `t(...)`), or the raw value unchanged when it matches nothing.
 * `projectTypeLabel` stays for callers that have no translator.
 */
export function projectTypeMsg(type: string | null | undefined): Msg | string {
  const n = normalizeProjectType(type);
  return PROJECT_TYPE_LABELS[n as ProjectType] ?? (n || '—');
}

/** True when `type` belongs in the bucket for `target`. */
export function isProjectType(type: string | null | undefined, target: ProjectType): boolean {
  return normalizeProjectType(type) === target;
}

/**
 * Count each declared type across a list of projects, plus the total. Every
 * project lands in exactly one bucket, so the buckets always sum to the total —
 * which is the property the old inline counts did not have.
 */
export function countProjectTypes(
  projects: Array<{ projectType?: string | null }>,
): Record<ProjectType, number> & { total: number; other: number } {
  const counts = Object.fromEntries(PROJECT_TYPES.map((t) => [t, 0])) as Record<ProjectType, number>;
  let other = 0;
  for (const p of projects) {
    const t = normalizeProjectType(p.projectType);
    if (PROJECT_TYPES.includes(t as ProjectType)) counts[t as ProjectType] += 1;
    else other += 1;
  }
  return { ...counts, total: projects.length, other };
}
